import type {
  ActingParty,
  ConveyedPersonAction,
  PersonInvolvement,
} from "../types/involvement.js";
import { carriesViewUpByInvolvementKind } from "./carriesViewUpByInvolvementKind.js";
import { isInvolvementLive } from "./isInvolvementLive.js";
import { personActionsPermittedByRole } from "./personActionsPermittedByRole.js";

/**
 * What an organisation's member organisations' involvements convey to a party
 * acting for it: reading the person, where a member holds a part in the
 * person's care that carries up, and the contents of their finished
 * assessments, where the organisation reads its members' assessments. Each as
 * far as the roles acted in permit. Nothing more: oversight reads, it does not
 * act.
 */
export const personActionsFromMemberOrganisations = (
  involvements: readonly Pick<
    PersonInvolvement,
    "kind" | "party" | "endedAt" | "expiresAt"
  >[],
  party: Pick<
    ActingParty,
    "roles" | "memberOrganisationIds" | "readsApprovedAssessmentsThroughMembers"
  >,
  now: Date = new Date(),
): ConveyedPersonAction[] => {
  const members = party.memberOrganisationIds ?? [];
  const reachedThroughAMember = involvements.some(
    (involvement) =>
      isInvolvementLive(involvement, now) &&
      carriesViewUpByInvolvementKind[involvement.kind] &&
      involvement.party.kind === "organisation" &&
      members.includes(involvement.party.organisationId),
  );
  const carried: readonly ConveyedPersonAction[] =
    party.readsApprovedAssessmentsThroughMembers === true
      ? ["read", "read_assessments"]
      : ["read"];
  const permitted = new Set(
    party.roles.flatMap((role) => personActionsPermittedByRole(role)),
  );

  return reachedThroughAMember && permitted.has("read")
    ? carried.filter((action) => permitted.has(action))
    : [];
};
